import React, { useEffect, useState } from 'react'
import {
  propEq,
  map,
  prop,
  isNil,
  compose,
  filter,
  isEmpty,
  includes,
  length,
  reduce,
  find,
  union,
  __,
  gt,
} from 'ramda'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import { TextField, CircularProgress, Chip } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CheckBox from 'app/componentV4/CheckBox'
import { useChannels } from './useChannels'
import { useField, useFormikContext } from 'formik'
import { usePlace } from 'commons/usePlace'
import {
  CHANNEL_APP,
  CHANNEL_SMS,
  CHANNEL_EMAIL,
  CHANNEL_CALL,
  FACEBOOK_VALUE,
  INSTAGRAM_VALUE,
  TWITTER_VALUE,
  LINKEDIN_VALUE,
  LINE_VALUE,
} from 'app/constants/channel'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import moment from 'moment'
import { TYPE_POLL } from 'app/constants/messageType'
import useFeatureFlag from 'app/configs/useFeatureFlag'
import CancelIcon from '@material-ui/icons/Cancel'
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined'
import { FormattedMessage } from 'react-intl'

const useStyle = makeStyles((theme) => ({
  root: {
    '& .MuiAutocomplete-inputRoot': {
      overflowX: 'auto',
      height: 'auto',
      maxHeight: '130px',
      minHeight: '48px',
      position: 'unset',
      padding: '5px 14px 5px 14px !important',
    },
  },
  rootChip: {
    '& .MuiChip-root': {
      margin: '.25rem',
      backgroundColor: '#0073b1',
      color: '#FFFFFF',
      height: 'auto',
    },
    '& .MuiChip-deleteIcon': {
      color: '#FFFFFF',
    },
    '& .MuiChip-label': {
      padding: '7px 12px',
    },
  },
  rootChipFlag: {
    '& .MuiChip-root': {
      margin: '.25rem',
      backgroundColor: '#183560',
      color: '#FFFFFF',
      height: 'auto',
    },
    '& .MuiChip-deleteIcon': {
      color: '#ffffff',
    },
    '& .MuiChip-label': {
      padding: '7px 12px',
    },
  },
  rootChipNewVersion: {
    '& .MuiChip-root': {
      margin: '.25rem',
      backgroundColor: '#ffffff',
      color: '#5D7290',
      height: 'auto',
      border: '1px solid #5D7290',
    },
    '& .MuiChip-deleteIcon': {
      color: '#5D7290',
    },
    '& .MuiChip-label': {
      padding: '7px 7px',
    },
  },
  subGroup: {
    textAlign: 'center',
    color: '#89f1ff',
  },
  textSubScribersCount: { color: theme.palette.primary.subtitle },
  avatarStyle: {
    width: '25px',
    height: '25px',
    fontSize: '13px',
  },
  optionSubScriberSubTitle: {
    padding: '10px 5px',
    width: '100%',
  },
  subScriberEmail: {
    color: '#a8a8a8',
    fontSize: '12px',
  },
}))

const ChannelSelector = (props) => {
  const {
    name,
    label,
    onChange,
    channelSetting,
    isLoadingCount,
    onChannelLoading,
    isBeaconDialog,
  } = props
  const { mes_3832_new_design_compose_message } = useFeatureFlag()
  const {
    state: { options, isLoading, isOpen },
    onOpenOption,
    onCloseOption,
    getOptions,
    updateOptions,
  } = useChannels()
  const classes = useStyle()
  const place = usePlace()
  const [field, meta, helpers] = useField(name)
  const { value } = field
  const [updateAt, setUpdateAt] = useState(undefined)

  const context = useFormikContext()
  const { values, setFieldValue } = context

  const isError = !isNil(meta.error) && meta.touched

  const getValueMatching = (ids) => {
    if (isEmpty(options)) return []
    const facebookOptionIds = compose(
      map(prop('id')),
      filter(propEq('parentId', FACEBOOK_VALUE)),
    )(options)

    const instagramOptionIds = compose(
      map(prop('id')),
      filter(propEq('parentId', INSTAGRAM_VALUE)),
    )(options)

    const valueFacebookIds = filter((id) => {
      return includes(id, facebookOptionIds)
    }, ids)

    const valueInstagramIds = filter((id) => {
      return includes(id, instagramOptionIds)
    }, ids)

    const notUseChannel =
      values.notiType === TYPE_POLL
        ? [FACEBOOK_VALUE, INSTAGRAM_VALUE, CHANNEL_CALL]
        : [FACEBOOK_VALUE, INSTAGRAM_VALUE]

    const newIds = filter((id) => {
      return !includes(id, notUseChannel)
    }, ids)

    return reduce(
      (acc, item) => {
        const optionParent = find((option) => {
          return item === option.parentId
        }, options)

        if (
          (optionParent !== undefined || item === FACEBOOK_VALUE) &&
          length(facebookOptionIds) === length(valueFacebookIds)
        ) {
          return acc
        }

        if (
          (optionParent !== undefined || item === INSTAGRAM_VALUE) &&
          length(instagramOptionIds) === length(valueInstagramIds)
        ) {
          return acc
        }

        const option = find((option) => {
          return values.notiType === TYPE_POLL
            ? option.id === item && option.id !== CHANNEL_CALL
            : option.id === item
        }, options)

        return [...acc, option]
      },
      [],
      newIds,
    )
  }

  const valueMatching = getValueMatching([
    ...value.channelIds,
    ...value.pageIds,
  ])

  const { setValue } = helpers

  const onDeleteValue = (
    option,
    channelIds,
    pageIds,
    facebookOption,
    instagramOption,
  ) => {
    if (option.id === FACEBOOK_VALUE) {
      channelIds = filter((id) => {
        return id !== option.id
      }, channelIds)
      pageIds = filter((id) => {
        return !includes(id, map(prop('id'), facebookOption))
      }, pageIds)
    }
    if (option.id === INSTAGRAM_VALUE) {
      channelIds = filter((id) => {
        return id !== option.id
      }, channelIds)
      pageIds = filter((id) => {
        return !includes(id, map(prop('id'), instagramOption))
      }, pageIds)
    }

    if (option.parentId === FACEBOOK_VALUE) {
      pageIds = filter((id) => {
        return id !== option.id
      }, pageIds)
      const pageValue = filter((item) => {
        return includes(item.id, pageIds)
      }, facebookOption)
      if (length(pageValue) === 0) {
        channelIds = filter((id) => {
          return id !== FACEBOOK_VALUE
        }, channelIds)
      }
    }
    if (option.parentId === INSTAGRAM_VALUE) {
      pageIds = filter((id) => {
        return id !== option.id
      }, pageIds)
      const pageValue = filter((item) => {
        return includes(item.id, pageIds)
      }, instagramOption)
      if (length(pageValue) === 0) {
        channelIds = filter((id) => {
          return id !== INSTAGRAM_VALUE
        }, channelIds)
      }
    }
    channelIds = filter((id) => {
      return id !== option.id
    }, channelIds)
    return { channelIds, pageIds }
  }

  const setValueForm = (selected, reason, option) => {
    let channelIds = value.channelIds
    let pageIds = value.pageIds
    let socialChannels = {
      channelIds,
      pageIds,
    }
    const facebookOption = filter(propEq('parentId', FACEBOOK_VALUE), options)

    const instagramOption = filter(propEq('parentId', INSTAGRAM_VALUE))(options)
    if (reason === 'select-option') {
      if (option.id === FACEBOOK_VALUE && facebookOption.length > 1) {
        channelIds = union(channelIds, map(prop('parentId'), facebookOption))
        pageIds = union(pageIds, map(prop('id'), facebookOption))
      }
      if (option.id === INSTAGRAM_VALUE && instagramOption.length > 1) {
        channelIds = union(channelIds, map(prop('parentId'))(instagramOption))
        pageIds = union(pageIds, map(prop('id'), instagramOption))
      }

      if (option.parentId === undefined || option.parentId === null) {
        channelIds = union(channelIds, [option.id])
      }

      if (includes(option.parentId, [FACEBOOK_VALUE, INSTAGRAM_VALUE])) {
        channelIds = union(channelIds, [option.parentId])
        pageIds = union(pageIds, [option.id])
      }
      socialChannels = { channelIds, pageIds }
    }
    if (reason === 'remove-option') {
      socialChannels = onDeleteValue(
        option,
        channelIds,
        pageIds,
        facebookOption,
        instagramOption,
      )
    }
    setValue(socialChannels)
    setUpdateAt(moment().format())
    if (onChange) {
      onChange(socialChannels)
    }
  }

  const handleDeleteValue = (option) => {
    let channelIds = value.channelIds
    let pageIds = value.pageIds
    const facebookOption = filter(propEq('parentId', FACEBOOK_VALUE), options)

    const instagramOption = filter(propEq('parentId', INSTAGRAM_VALUE))(options)
    const socialChannels = onDeleteValue(
      option,
      channelIds,
      pageIds,
      facebookOption,
      instagramOption,
    )

    setValue(socialChannels)
    setUpdateAt(moment().format())
    if (onChange) {
      onChange(socialChannels)
    }
  }

  const getInput = (selected) => {
    let newSelected = selected
    const faceBookOptionIds = compose(
      map(prop('id')),
      filter(propEq('parentId', FACEBOOK_VALUE)),
    )(options)
    const selectedFacebookValue = compose(
      map(prop('id')),
      filter(propEq('parentId', FACEBOOK_VALUE)),
    )(selected)

    if (
      faceBookOptionIds.length === selectedFacebookValue.length &&
      faceBookOptionIds.length > 1
    ) {
      newSelected = filter((item) => {
        return !(parseInt(item.parentId) === FACEBOOK_VALUE)
      }, selected)
      const facebookOption = find(propEq('id', FACEBOOK_VALUE), options)

      newSelected = [...newSelected, facebookOption]
    }

    const instagramOptionIds = compose(
      map(prop('id')),
      filter(propEq('parentId', INSTAGRAM_VALUE)),
    )(options)
    const selectedInstagramValue = compose(
      map(prop('id')),
      filter(propEq('parentId', INSTAGRAM_VALUE)),
    )(selected)

    if (
      instagramOptionIds.length === selectedInstagramValue.length &&
      instagramOptionIds.length > 1
    ) {
      newSelected = filter((item) => {
        return !(parseInt(item.parentId) === INSTAGRAM_VALUE)
      }, selected)
      const instagramOption = find(propEq('id', INSTAGRAM_VALUE), options)

      newSelected = [...newSelected, instagramOption]
    }

    return newSelected
  }

  const handleRenderInput = (selected) => {
    if (isEmpty(selected)) return
    const inputs = getInput(selected)

    return inputs.map((option) => {
      return (
        <div
          className={
            mes_3832_new_design_compose_message
              ? classes.rootChipNewVersion
              : classes.rootChipFlag
          }
          key={option.id}
        >
          <Chip
            label={
              <div className="flex items-center space-x-2">
                {option.icon}
                <div>{option.name}</div>
              </div>
            }
            onDelete={() => {
              handleDeleteValue(option)
            }}
            deleteIcon={
              mes_3832_new_design_compose_message ? (
                <CloseOutlinedIcon fontSize="small" />
              ) : (
                <CancelIcon />
              )
            }
          />
        </div>
      )
    })
  }

  const handleRenderOption = (option, { selected }) => {
    if (!(option.id === CHANNEL_CALL && values.notiType === TYPE_POLL)) {
      return (
        <div
          className={clsx(
            option.isSubMenu ? 'ml-5' : '-ml-3 p-0',
            'flex',
            'items-baseline',
          )}
        >
          <CheckBox checked={selected} />

          <div className="flex my-auto">
            <div className="flex items-center px-2">{option.iconRevert}</div>
            {option.name}
            {!option.isSubMenu &&
            includes(option.parentId, [FACEBOOK_VALUE, INSTAGRAM_VALUE])
              ? ` - ${option.subMenu}`
              : ''}
            {!option.isSubMenu && includes(option.id, [LINE_VALUE]) ? (
              <div className="px-2 text-red">
                (
                <FormattedMessage
                  id="delivery-quota-line"
                  values={{ usaged: option.usaged, credits: option.credits }}
                />
                )
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
      )
    }
  }

  const handleGetOptions = () => {
    getOptions({ placeId: place.id })
  }

  const handleOptionSelected = (option, item) => {
    const isFacebook = option.id === FACEBOOK_VALUE
    const faceBookOptionIds = compose(
      map(prop('id')),
      filter(propEq('parentId', FACEBOOK_VALUE)),
    )(options)

    const checkAllFacebook = filter((pageId) => {
      return includes(pageId, faceBookOptionIds)
    }, value.pageIds)

    if (isFacebook && checkAllFacebook.length === faceBookOptionIds.length) {
      return true
    }
    const isInstagram = option.id === INSTAGRAM_VALUE
    const instagramOptionIds = compose(
      map(prop('id')),
      filter(propEq('parentId', INSTAGRAM_VALUE)),
    )(options)

    const checkAllInstagram = filter((pageId) => {
      return includes(pageId, instagramOptionIds)
    }, value.pageIds)

    if (isInstagram && checkAllInstagram.length === instagramOptionIds.length) {
      return true
    }

    return item.id === option.id
  }

  useEffect(handleGetOptions, [])

  const haldleLoading = () => {
    if (onChannelLoading) {
      onChannelLoading(isLoading)
    }
  }

  useEffect(haldleLoading, [isLoading])

  const handleSettingOption = () => {
    if (options.length > 0 && !isNil(channelSetting)) {
      const newOptions = map((item) => {
        if (item.id === CHANNEL_APP) {
          item.disabled = !channelSetting.app
        }
        if (item.id === CHANNEL_SMS) {
          item.disabled = !channelSetting.sms
        }
        if (item.id === CHANNEL_EMAIL) {
          item.disabled = !channelSetting.email
        }
        if (item.id === CHANNEL_CALL) {
          item.disabled = !channelSetting.call || isBeaconDialog
        }
        return item
      }, options)

      updateOptions(newOptions)
    }
  }
  useEffect(handleSettingOption, [channelSetting, options.length])

  const setFieldWhenChannelChange = () => {
    if (!isNil(updateAt)) {
      const channelIds = values.channels.channelIds
      const pageIds = values.channels.pageIds
      const hasSharing = compose(
        gt(__, 0),
        length,
        filter(
          includes(__, [
            CHANNEL_EMAIL,
            CHANNEL_SMS,
            TWITTER_VALUE,
            LINKEDIN_VALUE,
            LINE_VALUE,
          ]),
        ),
      )(channelIds)

      const isSharingEnabled = !isEmpty(pageIds) || hasSharing
      if (!isBeaconDialog) {
        setFieldValue('isSharingEnabled', isSharingEnabled)
      }

      if (!isSharingEnabled) {
        setFieldValue('isDisplayHistory', false)
      }

      if (includes(CHANNEL_CALL, value.channelIds)) {
        setFieldValue('isTtsEnabled', values.notiType !== TYPE_POLL)
      }
    }
  }

  useEffect(setFieldWhenChannelChange, [setFieldValue, updateAt])

  return (
    <>
      <Autocomplete
        {...field}
        name={name}
        id={`${name}-autocomplete`}
        multiple
        open={isOpen}
        onOpen={() => onOpenOption}
        onClose={() => onCloseOption}
        disableCloseOnSelect={true}
        value={valueMatching}
        options={options}
        groupBy={(option) => option.groupBy}
        getOptionLabel={(option) => option.name}
        disabled={isLoading}
        getOptionDisabled={(option) => option.disabled}
        getOptionSelected={handleOptionSelected}
        renderOption={handleRenderOption}
        renderTags={handleRenderInput}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              placeholder={isEmpty(valueMatching) ? label : ''}
              className="w-full"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <React.Fragment>
                    {(isLoading || isLoadingCount) && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                  </React.Fragment>
                ),
              }}
            />
          </>
        )}
        onChange={(event, value, reason, { option }) => {
          setValueForm(value, reason, option)
        }}
        className={clsx(
          classes.root,
          'bg-white rounded-8',
          isError
            ? 'border border-red bg-white'
            : 'border border-grey-input text-grey-100',
        )}
      />
      {isError && (
        <div className="flex space-x-2 py-2">
          <div className="flex my-auto">
            <ErrorOutlineIcon className="text-red-500" fontSize="small" />
          </div>
          <label className="text-red">{meta.error}</label>
        </div>
      )}
    </>
  )
}

export default ChannelSelector
